import React, { useState, useEffect } from "react";
import { X, AlertCircle, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_BASE_URL;


// ── Helper functions for worker_data API response shape ───────────────────────
const getPaymentData = (record) => {
  if (!record) return {};
  if (record.dailypayment && record.dailypayment.length > 0) return record.dailypayment[0];
  return record.payment || {};
};

const getStatus = (record) => {
  if (!record) return "";
  if (record.dailypayment?.[0]?.status) return record.dailypayment[0].status.toLowerCase();
  if (record.attendance?.status) return record.attendance.status.toLowerCase();
  return record.PaymentStatus?.toLowerCase() || "";
};

const getShiftId = (record) => {
  if (!record) return "";
  if (record.attendance?._id) return record.attendance._id;
  if (record.attendance?.id) return record.attendance.id;
  return record.attendanceId || "";
};

// ── PaymentEditModal ──────────────────────────────────────────────────────────
const PaymentEditModal = ({ isOpen, onClose, record, onSubmit, isPayoutFilePage ,editInsidePayoutFileBy}) => {

  const [formData, setFormData] = useState({
    currentAmount: 0,
    deductedAmount: 0,
    newAmount: "",
    remarks: "",
    paymentOnHold: false,
    paidByCash: false,
    editInsidePayoutFile: false,
    editInsidePayoutFileBy:"",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationError, setValidationError] = useState("");

  const rawStatus = getStatus(record);

let isPending;


if (isPayoutFilePage==true) {
  isPending =
    rawStatus === "pending" ||
    rawStatus === "on-hold" ||
    rawStatus === "payoutgenerated";
} else {
  isPending =
    rawStatus === "pending" ||
    rawStatus === "on-hold";
}

  useEffect(() => {
    if (record && isOpen) {
      const payment = getPaymentData(record);
      const status = getStatus(record);

      setFormData({
        currentAmount: payment.grossAmount || payment.amount || 0,
        deductedAmount: payment.DeductedAmount ?? payment.deductedAmount ?? 0,
        newAmount: payment.grossAmount || payment.amount || 0,
        remarks: payment.remarks || payment.remark || payment.transactionRemarks || "",
        paymentOnHold: payment.PaymentOnHold || status === "on-hold" || false,
        paidByCash: payment.paidByCash || false,
        editInsidePayoutFile: !!isPayoutFilePage,
        editInsidePayoutFileBy:editInsidePayoutFileBy // ✅ explicitly set from prop
      });
      setError("");
      setValidationError("");
    }

    if (!isOpen) {
      setFormData({
        currentAmount: 0,
        deductedAmount: 0,
        newAmount: "",
        remarks: "",
        paymentOnHold: false,
        paidByCash: false,
        editInsidePayoutFile: false,
        editInsidePayoutFileBy:editInsidePayoutFileBy
      });
      setError("");
      setValidationError("");
    }
  }, [isOpen, record, isPayoutFilePage]); // ✅ isPayoutFilePage in deps

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newFormData = {
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    };
    setFormData(newFormData);
    if (name === "newAmount") validateAmount(newFormData.newAmount, newFormData.deductedAmount);
    if (error) setError("");
  };

  const validateAmount = (newAmount, deductedAmount) => {
    const newAmountNum = parseFloat(newAmount) || 0;
    const deductedAmountNum = parseFloat(deductedAmount) || 0;
    if (newAmountNum <= deductedAmountNum) {
      setValidationError(
        `New gross amount (₹${newAmountNum}) must be greater than deductions (₹${deductedAmountNum})`
      );
      return false;
    }
    setValidationError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setValidationError("");

    if (!formData.remarks.trim()) { setError("Remarks are required"); return; }

    if (!formData.paidByCash) {
      if (!formData.newAmount || parseFloat(formData.newAmount) < 0) {
        setError("Please enter a valid amount"); return;
      }
      const newAmountNum = parseFloat(formData.newAmount);
      const deductedAmountNum = parseFloat(formData.deductedAmount);
      if (newAmountNum <= deductedAmountNum) {
        setValidationError(
          `New gross amount (₹${newAmountNum}) must be greater than deductions (₹${deductedAmountNum})`
        );
        return;
      }
    }

    setLoading(true);
    try {
      await onSubmit({
        shiftId: getShiftId(record),
        ...(!formData.paidByCash &&
          parseFloat(formData.newAmount) !== parseFloat(formData.currentAmount) && {
            newAmount: parseFloat(formData.newAmount),
          }),
        remarks: formData.remarks.trim(),
        PaymentOnHold: formData.paymentOnHold,
        PaidByCash: formData.paidByCash,
        ...(formData.editInsidePayoutFile && { editInsidePayoutFile: true,editInsidePayoutFileBy }), //  from formData
      });
      onClose();
    } catch (err) {
      setError(err.message || "Failed to update payment amount");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            {isPending ? "Update Payment Amount" : "View Payment Details"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Current Gross Amount */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Gross Amount</label>
            <input
              type="text"
              value={`₹${formData.currentAmount}`}
              disabled
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
            />
          </div>

          {/* Current Deductions */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Deductions</label>
            <input
              type="text"
              value={`₹${formData.deductedAmount}`}
              disabled
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
            />
          </div>

          {isPending && !formData.paidByCash ? (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Gross Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">₹</span>
                  <input
                    type="number"
                    name="newAmount"
                    value={formData.newAmount}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    disabled={loading}
                    className={`w-full pl-7 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed ${
                      validationError ? "border-red-300" : "border-gray-300"
                    }`}
                  />
                </div>
                {validationError && <p className="mt-1 text-sm text-red-600">{validationError}</p>}
              </div>

              {/* Final Amount Preview */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-blue-800">Final Amount:</span>
                  <span className="text-lg font-bold text-blue-800">
                    ₹{(parseFloat(formData.newAmount) || 0) - (parseFloat(formData.deductedAmount) || 0)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-blue-600">(New Gross - Deductions)</p>
              </div>
            </>
          ) : (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Final Amount (after deductions)
              </label>
              <input
                type="text"
                value={`₹${(formData.currentAmount || 0) - (formData.deductedAmount || 0)}`}
                disabled
                className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
              />
            </div>
          )}

          {/* Checkboxes */}
          <div className="mb-4 space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="paymentOnHold"
                checked={formData.paymentOnHold}
                onChange={handleInputChange}
                disabled={!isPending}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <span className="ml-2 text-sm text-gray-700">Payment On Hold</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="paidByCash"
                checked={formData.paidByCash}
                onChange={handleInputChange}
                disabled={!isPending}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <span className="ml-2 text-sm text-gray-700">Paid By Cash</span>
            </label>
          </div>

          {/* Remarks */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks {isPending && <span className="text-red-500">*</span>}
            </label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleInputChange}
              placeholder={isPending ? "Enter reason for payment modification..." : ""}
              rows="3"
              disabled={!isPending}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* Footer */}
          {isPending ? (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || (!formData.paidByCash && !!validationError)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center disabled:bg-blue-400"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Updating...
                  </>
                ) : (
                  "Update Payment"
                )}
              </button>
            </div>
          ) : (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

// ── Confirmation Modal ────────────────────────────────────────────────────────
const ConfirmationModal = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">Confirm Update</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="p-4">
          <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 shrink-0" />
            <p className="text-sm text-yellow-800">
              Are you sure you want to update the status of this payment to{" "}
              <span className="font-semibold">pending</span>? This action cannot be undone.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Success Modal ─────────────────────────────────────────────────────────────
const SuccessModal = ({ isOpen, message, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100]">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
        <div className="flex flex-col items-center text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Success!</h3>
          <p className="text-gray-600 mb-6">{message}</p>
          <button onClick={onClose} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Error Modal ───────────────────────────────────────────────────────────────
const ErrorModal = ({ isOpen, message, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100]">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
        <div className="flex flex-col items-center text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Error</h3>
          <p className="text-gray-600 mb-6">{message}</p>
          <button onClick={onClose} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// ── HandlePayment ─────────────────────────────────────────────────────────────
const HandlePayment = ({
  paymentEditModal,
  hasPayoutGen,
  hasPayoutApproval,
  selectedStatus,
  setPaymentEditModal,
  showConfirmationModal,
  setShowConfirmationModal,
  selectedAttendanceId,
  setSelectedAttendanceId,
  retryingPayments,
  setRetryingPayments,
  currentPage,
  fetchAttendanceData,
  setAttendanceData,
}) => {
  const location = useLocation();
  const isPayoutFilePage = location.pathname === "/lkart/superadmin/payout-file"; // ✅ clean check

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const path = window.location.pathname;

  let editInsidePayoutFileBy;

if (selectedStatus=="Payout Generator") {
  editInsidePayoutFileBy = "file_generator";
} else if (selectedStatus="Payout Approver") {
  editInsidePayoutFileBy = "file_Approver";
}

  // ── Update payment amount ─────────────────────────────────────────────────
  const handleUpdatePayment = async (data) => {
    try {
      const token = path.startsWith("/lkart/admin")
  ? localStorage.getItem("admin_auth_token")
  : path.startsWith("/lkart/superadmin")
    ? localStorage.getItem("superadmintoken")
    : null;
      const response = await fetch(`${BASE_URL}/lenskart-admin/update/payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data), //  editInsidePayoutFile will be inside data
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error || "Failed to update payment");

      setModalMessage("Payment amount updated successfully");
      setShowSuccessModal(true);

fetchAttendanceData(currentPage, false);

      return result;
    } catch (error) {
      setModalMessage(error.message || "Error updating payment amount");
      setShowErrorModal(true);
      throw error;
    }
  };

  const confirmPendingUpdate = async () => {
    if (!selectedAttendanceId) return;
    try {
      setRetryingPayments((prev) => ({ ...prev, [selectedAttendanceId]: true }));
      const token = path.startsWith("/lkart/admin")
  ? localStorage.getItem("admin_auth_token")
  : path.startsWith("/lkart/superadmin")
    ? localStorage.getItem("superadmintoken")
    : null;
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/update-payment-status`,
        { shiftId: selectedAttendanceId, status: "pending" },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (response.data.success) {
        setAttendanceData((prevData) =>
          prevData.map((record) =>
            record.attendanceId === selectedAttendanceId
              ? { ...record, payment: { ...record.payment, status: "pending" } }
              : record
          )
        );
        setModalMessage("Payment status updated to pending successfully");
        setShowSuccessModal(true);
      } else {
        setModalMessage(response.data.message || "Failed to update payment status");
        setShowErrorModal(true);
      }
    } catch (error) {
      setModalMessage(
        error.response?.data?.message || "Error updating payment status. Please try again."
      );
      setShowErrorModal(true);
    } finally {
      setRetryingPayments((prev) => {
        const newState = { ...prev };
        delete newState[selectedAttendanceId];
        return newState;
      });
      setShowConfirmationModal(false);
      fetchAttendanceData(currentPage, false);
      setSelectedAttendanceId(null);
    }
  };

  const cancelPendingUpdate = () => {
    setShowConfirmationModal(false);
    setSelectedAttendanceId(null);
  };

  return (
    <>
      <PaymentEditModal
        key={
          (paymentEditModal.record?.attendance?._id ||
            paymentEditModal.record?.attendanceId || "") +
          "-" +
          (paymentEditModal.record?.dailypayment?.[0]?.updatedAt || "")
        }
        isOpen={paymentEditModal.isOpen}
        onClose={() => setPaymentEditModal({ isOpen: false, record: null })}
        record={paymentEditModal.record}
        isPayoutFilePage={isPayoutFilePage} // ✅ passed down
        onSubmit={handleUpdatePayment}
        editInsidePayoutFileBy={editInsidePayoutFileBy}
      />

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onConfirm={confirmPendingUpdate}
        onCancel={cancelPendingUpdate}
      />

      <SuccessModal
        isOpen={showSuccessModal}
        message={modalMessage}
        onClose={() => setShowSuccessModal(false)}
      />

      <ErrorModal
        isOpen={showErrorModal}
        message={modalMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </>
  );
};

export default HandlePayment;